import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, 
  User, 
  BookOpen, 
  Calendar, 
  Settings, 
  LogOut,
  Building2,
  Users,
  FileText,
  BarChart3,
  CreditCard
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logophone from '../../assets/logophone.png';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const isTrainingCenter = user?.role?.name === 'training_center' || user?.role_id === 2;

  const handleLogout = () => {
    if (window.confirm(t('trainee.navigation.logoutConfirm'))) {
      logout();
      navigate('/');
    }
  };

  const trainingCenterNavItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/training-center/dashboard' },
    { icon: BookOpen, label: 'Formations', path: '/training-center/trainings' },
    { icon: Users, label: 'Participants', path: '/training-center/participants' },
    { icon: Calendar, label: 'Sessions', path: '/training-center/sessions' },
    { icon: BarChart3, label: 'Statistiques', path: '/training-center/stats' },
    { icon: CreditCard, label: 'Factures', path: '/training-center/invoices' },
    { icon: CreditCard, label: 'Abonnements', path: '/training-center/subscriptions' },
    { icon: User, label: 'Profil', path: '/training-center/profile' },
    { icon: Settings, label: 'Paramètres', path: '/training-center/settings' },
  ];

  const traineeNavItems = [
    { icon: LayoutDashboard, label: t('trainee.navigation.dashboard'), path: '/dashboard' },
    { icon: BookOpen, label: t('trainee.navigation.catalog'), path: '/trainings' },
    { icon: Calendar, label: t('trainee.navigation.myTrainings'), path: '/my-trainings' },
    { icon: User, label: t('trainee.navigation.profile'), path: '/profile' },
  ];

  const navItems = isTrainingCenter ? trainingCenterNavItems : traineeNavItems;

  return (
    <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-2xl border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:inset-0`}>
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center justify-center h-16 px-4 border-b border-gray-200 bg-gradient-to-r from-brand-primary/5 to-brand-accent/5">
          <div className="flex items-center space-x-3">
            <img
              src={logophone}
              alt="PHINLEX Logo"
              className="h-10 w-10 rounded-xl object-cover border-2 border-brand-primary/20 shadow-lg"
            />
            <div>
              <span className="text-lg font-bold text-gray-900">PHINLEX</span>
              <p className="text-xs text-gray-500">Formation</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="px-4 py-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
              <span className="text-brand-primary font-semibold text-sm">
                {user?.fullName?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.fullName || user?.email}
              </p>
              <p className="text-xs text-gray-500 capitalize">
                {isTrainingCenter ? t('trainee.navigation.userType.trainingCenter') : t('trainee.navigation.userType.trainee')}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-brand-primary to-brand-primary/80 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-gradient-to-r hover:from-brand-primary/10 hover:to-brand-accent/10 hover:text-brand-primary hover:shadow-md'
                }`}
                onClick={() => {
                  // Close sidebar on mobile after navigation
                  if (window.innerWidth < 1024) {
                    onToggle();
                  }
                }}
              >
                <Icon className={`h-5 w-5 transition-colors ${isActive ? 'text-white' : 'text-brand-primary group-hover:text-brand-primary'}`} />
                <span className="transition-colors">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="px-4 py-4 border-t border-gray-200 bg-gray-50/50">
          <button
            onClick={handleLogout}
            className="group flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 hover:text-red-700 transition-all duration-200 hover:shadow-md"
          >
            <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform" />
            <span>{t('trainee.navigation.logout')}</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
